import React, { useEffect, useState } from 'react';

const Toast = ({ message, type = 'success', duration = 10000, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 400); // Wait for slide-out animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]); // Remove onClose from dependencies to prevent timer reset

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-surface',
          border: 'border-fun-mint',
          text: 'text-emerald-800 dark:text-emerald-200',
          iconBg: 'bg-emerald-100 dark:bg-emerald-900/40',
          emoji: '✨',
          label: 'Success'
        };
      case 'error':
        return {
          bg: 'bg-surface',
          border: 'border-fun-coral',
          text: 'text-rose-800 dark:text-rose-200',
          iconBg: 'bg-rose-100 dark:bg-rose-900/40',
          emoji: '😅',
          label: 'Oops'
        };
      case 'info':
        return {
          bg: 'bg-surface',
          border: 'border-fun-sky',
          text: 'text-sky-800 dark:text-sky-200',
          iconBg: 'bg-sky-100 dark:bg-sky-900/40',
          emoji: '💡',
          label: 'Info'
        };
      case 'warning':
        return {
          bg: 'bg-surface',
          border: 'border-fun-lemon',
          text: 'text-amber-800 dark:text-amber-200',
          iconBg: 'bg-amber-100 dark:bg-amber-900/40',
          emoji: '⚠️',
          label: 'Warning'
        };
      default:
        return {
          bg: 'bg-surface',
          border: 'border-fun-mint',
          text: 'text-emerald-800 dark:text-emerald-200',
          iconBg: 'bg-emerald-100 dark:bg-emerald-900/40',
          emoji: '✨',
          label: 'Success'
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`fixed top-4 right-4 z-50 max-w-sm
        ${styles.bg} ${styles.text}
        rounded-clay-lg border-3 ${styles.border} shadow-clay overflow-hidden
        transition-all duration-400 ease-bounce-in
        ${isVisible
          ? 'opacity-100 translate-y-0 scale-100'
          : 'opacity-0 -translate-y-4 scale-95'
        }`}
    >
      <div className="flex items-start gap-3 p-4">
        {/* Emoji icon with background */}
        <div className={`flex-shrink-0 w-10 h-10 ${styles.iconBg} rounded-clay
          flex items-center justify-center text-xl`}>
          <span role="img" aria-label={styles.label}>{styles.emoji}</span>
        </div>

        {/* Message content */}
        <div className="flex-1 pt-1">
          <p className="font-body font-medium leading-snug">{message}</p>
        </div>

        {/* Close button */}
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 400);
          }}
          className="flex-shrink-0 p-1.5 text-tertiary-text hover:text-secondary-text
            hover:bg-surface-hover rounded-clay transition-all duration-200"
          aria-label="Dismiss notification"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-surface-hover rounded-b-clay overflow-hidden">
        <div
          className={`h-full ${type === 'error' ? 'bg-fun-coral' : type === 'info' ? 'bg-fun-sky' : type === 'warning' ? 'bg-fun-lemon' : 'bg-fun-mint'}`}
          style={{
            animation: `shrink ${duration}ms linear forwards`
          }}
        />
      </div>

      {/* Animation keyframe for progress bar */}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default Toast;
